// The privacy policy — a plain-language, COPPA-aware notice for the grown-ups.
// BibleRacers is directed to children, so COPPA applies: this page is the
// required online notice (16 CFR §312.4). It lives at its own URL (/privacy)
// so it can be linked prominently from the homepage and from the spots where a
// name is collected. Keep it truthful to what the app actually does — if data
// practices change (e.g. analytics are added), update this page in the same PR.
import DeleteRacerForm from "../components/DeleteRacerForm";
import SceneBackground from "../components/brand/SceneBackground";
import Wordmark from "../components/brand/Wordmark";

// Single source of truth for the parent-facing contact — a working contact is a
// COPPA requirement, so keep this pointed at a monitored inbox.
const CONTACT_EMAIL = "hello@bibleracers.com";
const LAST_UPDATED = "August 17, 2026";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-7">
      <h2 className="font-display text-xl text-ink sm:text-2xl">{title}</h2>
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-ink/70">{children}</div>
    </section>
  );
}

export default function Privacy({ onHome }: { onHome: () => void }) {
  return (
    <div className="relative min-h-full">
      <SceneBackground showSpeed={false} />
      <main className="mx-auto w-full max-w-2xl px-4 pb-16 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <button type="button" className="btn-chunk bg-white px-4 py-2 text-sm" onClick={onHome}>
            ← Home
          </button>
        </div>
        <div className="mb-4 flex justify-center">
          <Wordmark width={200} />
        </div>

        <div className="panel p-5 sm:p-7">
          <h1 className="font-display text-3xl text-ink">Privacy Policy</h1>
          <p className="mt-1 text-xs font-bold uppercase tracking-wide text-ink/45">
            Last updated {LAST_UPDATED}
          </p>

          <p className="mt-4 text-sm leading-relaxed text-ink/70">
            BibleRacers is a Bible trivia racing game made for kids and families. We
            built it to be playable in seconds — no account, no email, and no real
            name. This page explains, in plain language for grown-ups, the little we
            collect and why.
          </p>

          <Section title="The short version">
            <ul className="ml-5 list-disc space-y-1.5">
              <li>No account, no email, and no real name needed to play.</li>
              <li>No ads, and no third-party advertising or tracking companies.</li>
              <li>
                We keep only what's needed to run the game: a chosen nickname, an
                avatar, and game scores/badges.
              </li>
              <li>
                Nicknames appear on public leaderboards — please pick a nickname,{" "}
                <strong>not a real or full name</strong>.
              </li>
              <li>
                Parents can ask us to review or delete a child's data anytime —{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="font-bold text-grape underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </li>
            </ul>
          </Section>

          <Section title="Information we collect">
            <ul className="ml-5 list-disc space-y-1.5">
              <li>
                <strong>Nickname &amp; avatar.</strong> A racer name and emoji you pick.
                Please use a fun nickname, not a real or full name.
              </li>
              <li>
                <strong>Game results.</strong> Scores, accuracy, times, badges, and
                daily streaks — so we can show your stats and leaderboards.
              </li>
              <li>
                <strong>Optional "save my racer."</strong> If you choose to save a
                profile so it works on another device, you pick a handle and a secret
                recovery word. The recovery word is stored only as a scrambled (hashed)
                value — we never see the original. No email is used or required.
              </li>
              <li>
                <strong>A random ID in your browser.</strong> So we can remember which
                racer is playing on a device and keep scores attached to the right
                profile, we store random identifiers in your browser and a matching
                random profile ID on our server. These are random — they are not tied
                to your real-world identity.
              </li>
              <li>
                <strong>Anonymous play counts.</strong> We keep simple day-by-day
                tallies of how the game is used: how often it's opened, which game
                modes are chosen, how many races are started and finished, and how
                often features like sharing a result or saving a racer are used.
                These are just totals — not tied to any child, profile, or device —
                and they help us see whether the game is fun and easy to use.
              </li>
            </ul>
          </Section>

          <Section title="What we do NOT collect">
            <ul className="ml-5 list-disc space-y-1.5">
              <li>No email addresses, phone numbers, or home addresses.</li>
              <li>No precise location.</li>
              <li>No photos, videos, or voice recordings.</li>
              <li>
                No advertising IDs, and no third-party advertising or analytics
                trackers. (The only usage data we keep is the anonymous, aggregate
                counts described above.)
              </li>
            </ul>
          </Section>

          <Section title="How we use this information">
            <p>
              We use it only to run the game: to start and time races, save and rank
              scores, award badges, and let you restore a saved racer on another device.
              We do not use it to advertise to your child, to build marketing profiles,
              or to contact your child. We also keep anonymous, aggregate counts (game
              opens, modes chosen, races started and finished, and feature use) to
              understand how the game is used and improve it — these aren't linked to
              any child. Under COPPA, this is "support for the internal operations" of
              the service.
            </p>
          </Section>

          <Section title="Public leaderboards">
            <p>
              The nickname attached to a score is shown publicly on leaderboards and in
              any result you choose to share. For that reason, please choose a nickname
              rather than a real or full name. We don't display any other information
              next to a score.
            </p>
          </Section>

          <Section title="Children's privacy (COPPA)">
            <p>
              BibleRacers is directed to children, so we treat every player as a child
              and apply the protections of the U.S. Children's Online Privacy Protection
              Act (COPPA) to everyone: we collect as little as possible, use it only to
              run the game, and allow no third-party advertising or tracking. Parents
              have the rights described below.
            </p>
          </Section>

          <Section title="Service providers">
            <p>
              We rely on a few trusted infrastructure providers to run the game. They
              process data only to provide their service to us:
            </p>
            <ul className="ml-5 list-disc space-y-1.5">
              <li>
                <strong>Netlify</strong> — website hosting and serverless functions.
              </li>
              <li>
                <strong>Neon</strong> — the database that stores profiles, scores, and
                badges.
              </li>
              <li>
                <strong>Ably</strong> — real-time updates during multiplayer races (only
                when this feature is enabled).
              </li>
            </ul>
            <p>
              We do not sell or rent any information, and we do not share it for
              advertising.
            </p>
          </Section>

          <Section title="How long we keep it">
            <p>
              We keep data only as long as it's useful for running leaderboards and
              profiles — never indefinitely. A daily cleanup automatically deletes data
              once it passes the limits below. You can also ask us to delete a racer
              sooner, at any time (see below).
            </p>
            <ul className="ml-5 list-disc space-y-1.5">
              <li>
                <strong>Anonymous racers</strong> (not saved with a handle) are removed
                after <strong>180 days</strong> with no play.
              </li>
              <li>
                <strong>Saved racers</strong> (saved with a handle + recovery word) are
                kept longer so they're ready when you come back — but not forever. They're
                removed after about <strong>24 months (730 days)</strong> with no play.
              </li>
              <li>
                <strong>Daily Challenge scores</strong> are kept for <strong>90 days</strong>.
              </li>
              <li>
                <strong>Guest leaderboard scores</strong> (posted without a saved racer, so
                they can't be restored) are removed after <strong>180 days</strong>.
              </li>
              <li>
                <strong>Anonymous play counts</strong> (the day-by-day totals, not tied to
                any child) are kept for <strong>180 days</strong>.
              </li>
            </ul>
            <p>
              Deleting a racer removes its profile, scores, and badges from our servers
              straight away.
            </p>
          </Section>

          <Section title="Parents' rights and choices">
            <p>
              <strong>To review</strong> what we hold: open the racer's profile in the
              app. Because we keep so little, that screen shows everything we store —
              nickname, avatar, scores, and badges.
            </p>
            <p>
              <strong>To delete</strong> a child's data, you have three options:
            </p>
            <ul className="ml-5 list-disc space-y-1.5">
              <li>
                <strong>On the device:</strong> open the racer's profile and tap "Delete
                this racer." This removes the profile, scores, and badges from our
                servers.
              </li>
              <li>
                <strong>A saved racer, from any device:</strong> if it was saved with a
                handle + recovery word, delete it right here — no account needed.
              </li>
              <li>
                <strong>Email us:</strong> contact{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="font-bold text-grape underline">
                  {CONTACT_EMAIL}
                </a>{" "}
                with the racer's handle or nickname and we'll locate and delete it.
              </li>
            </ul>
            <div className="mt-3 rounded-2xl border-[3px] border-dashed border-ink/20 bg-white p-3">
              <div className="text-sm font-bold text-ink">Delete a saved racer</div>
              <p className="mt-0.5 text-xs text-ink/55">
                Enter the handle and recovery word you chose when saving the racer.
              </p>
              <div className="mt-2">
                <DeleteRacerForm />
              </div>
            </div>
          </Section>

          <Section title="Security">
            <p>
              Recovery words are stored only as salted, hashed values, and game actions
              are protected with signed tokens. No method is 100% secure, but we limit
              what we collect in the first place to keep risk low.
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>
              If we change this policy, we'll update the date at the top and post the new
              version here.
            </p>
          </Section>

          <Section title="Contact us">
            <p>
              Questions about privacy? Email{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-bold text-grape underline">
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}
